import axios, { AxiosError, AxiosResponse } from 'axios';
import type { ApiResponse } from '@/types';

// Create axios instance (can be swapped in tests)
let api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const setApiClient = (client: typeof api) => {
  api = client;
};

const buildQueryString = (params?: Record<string, unknown>) => {
  if (!params) return '';
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null) return;
        searchParams.append(key, String(item));
      });
      return;
    }
    searchParams.append(key, String(value));
  });
  return searchParams.toString();
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic API call wrapper
export const apiCall = async <T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  endpoint: string,
  data?: any
): Promise<ApiResponse<T>> => {
  try {
    const response = await api.request({
      method,
      url: endpoint,
      data,
    });

    // Return the payload as-is for callers/tests
    if (response.data !== undefined) {
      return response.data as ApiResponse<T>;
    }

    // Handle empty responses (e.g., 204)
    return { success: true } as ApiResponse<T>;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return error.response.data as ApiResponse<T>;
    }
    return {
      success: false,
      error: {
        name: 'NetworkError',
        message: 'Network error occurred',
      },
    };
  }
};

// Specific API functions
export const authApi = {
  login: (email: string) => apiCall<any>('POST', '/auth/login', { email }),
  me: () => apiCall<any>('GET', '/auth/me'),
  logout: () => apiCall<any>('POST', '/auth/logout'),
};

export const planningPeriodsApi = {
  getAll: () => apiCall<any>('GET', '/planning-periods'),
  getById: (id: string) => apiCall<any>('GET', `/planning-periods/${id}`),
  create: (data: any) => apiCall<any>('POST', '/planning-periods', data),
  update: (id: string, data: any) => apiCall<any>('PUT', `/planning-periods/${id}`, data),
  activate: (id: string) => apiCall<any>('POST', `/planning-periods/${id}/activate`),
  close: (id: string) => apiCall<any>('POST', `/planning-periods/${id}/close`),
  delete: (id: string) => apiCall<any>('DELETE', `/planning-periods/${id}`),
};

export const investibleStructuresApi = {
  getAll: (params?: { planningPeriodId?: string; category?: string; skipScopeFilter?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/investible-structures${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/investible-structures/${id}`),
  create: (data: any) => apiCall('POST', '/investible-structures', data),
  update: (id: string, data: any) => apiCall('PUT', `/investible-structures/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/investible-structures/${id}`),
};

export const programsApi = {
  getAll: (params?: { category?: string; isActive?: boolean }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/programs${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/programs/${id}`),
  getAnalytics: (id: string) => apiCall('GET', `/programs/${id}/analytics`),
  create: (data: any) => apiCall('POST', '/programs', data),
  update: (id: string, data: any) => apiCall('PUT', `/programs/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/programs/${id}`),
};

export const teamsApi = {
  getAll: (params?: { programId?: string; isActive?: boolean }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/teams${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/teams/${id}`),
  create: (data: any) => apiCall('POST', '/teams', data),
  update: (id: string, data: any) => apiCall('PUT', `/teams/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/teams/${id}`),
};

export const costProfilesApi = {
  getAll: (params?: { isActive?: boolean }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/cost-profiles${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/cost-profiles/${id}`),
};

export const epicsApi = {
  getAll: (params?: {
    investibleStructureId?: string;
    status?: string;
    programId?: string;
    supplyingProgramId?: string;  // Filter EPICs where this program supplies capacity
    page?: number;
    limit?: number;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/epics${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/epics/${id}`),
  create: (data: any) => apiCall('POST', '/epics', data),
  update: (id: string, data: any) => apiCall('PUT', `/epics/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/epics/${id}`),
  updateStatus: (id: string, status: string) => apiCall('PUT', `/epics/${id}/status`, { status }),
  updatePriority: (id: string, priority: number) => apiCall('PUT', `/epics/${id}/priority`, { priority }),
  
  // Priority management endpoints
  getPriorityScore: (id: string, factors?: any) => {
    const query = buildQueryString(factors as any);
    return apiCall('GET', `/epics/${id}/priority-score${query ? `?${query}` : ''}`);
  },
  getPriorityRankings: (isId: string, includeScores?: boolean) => {
    const query = includeScores ? '?includeScores=true' : '';
    return apiCall('GET', `/epics/is/${isId}/priority-rankings${query}`);
  },
  autoAssignPriorities: (isId: string, factors?: any) => 
    apiCall('POST', `/epics/is/${isId}/auto-assign-priorities`, factors || {}),
  reorderPriorities: (isId: string, epicIds: string[]) => 
    apiCall('PUT', `/epics/is/${isId}/reorder-priorities`, { epicIds }),
  bulkUpdatePriorities: (updates: Array<{ epicId: string; newPriority: number }>) => 
    apiCall('PUT', '/epics/bulk-update-priorities', { updates }),
  validatePriorities: (isId: string) => 
    apiCall('GET', `/epics/is/${isId}/validate-priorities`),
  
  // Allocation management endpoints
  getAllocations: (epicId: string) => apiCall('GET', `/epics/${epicId}/allocations`),
  createAllocation: (epicId: string, data: any) => apiCall('POST', `/epics/${epicId}/allocations`, data),
  updateAllocation: (allocationId: string, data: any) => apiCall('PUT', `/allocations/${allocationId}`, data),
  deleteAllocation: (allocationId: string) => apiCall('DELETE', `/allocations/${allocationId}`),
};

export const featuresApi = {
  getAll: (params?: {
    epicId?: string;
    leadProgramId?: string;
    targetQuarter?: number;
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/features${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/features/${id}`),
  create: (data: any) => apiCall('POST', '/features', data),
  update: (id: string, data: any) => apiCall('PUT', `/features/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/features/${id}`),
  complete: (id: string) => apiCall('POST', `/features/${id}/complete`),
  reopen: (id: string) => apiCall('POST', `/features/${id}/reopen`),
};

export const featureReadinessApi = {
  getByFeatureId: (featureId: string) => apiCall('GET', `/feature-readiness/${featureId}`),
  updateReadinessItem: (featureId: string, itemId: string, isCompleted: boolean) => 
    apiCall('PATCH', `/feature-readiness/${featureId}/items/${itemId}`, { isCompleted }),
  bulkUpdateReadiness: (
    featureId: string,
    items: Array<{ item: string; isCompleted: boolean }>
  ) =>
    apiCall('PATCH', `/feature-readiness/${featureId}/bulk`, { items }),
  getQuarterlyReadiness: (params?: { quarter?: number; epicId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/feature-readiness/quarterly${query ? `?${query}` : ''}`);
  },
};

export const okrsApi = {
  // Legacy endpoints (for backward compatibility)
  getAll: (params?: { epicId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/okrs${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/okrs/${id}`),
  create: (data: any) => apiCall('POST', '/okrs', data),
  update: (id: string, data: any) => apiCall('PUT', `/okrs/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/okrs/${id}`),
  updateProgress: (id: string, currentValue: number) =>
    apiCall('PUT', `/okrs/${id}/progress`, { currentValue }),

  // Enhanced Objective endpoints
  objectives: {
    getAll: (params?: {
      level?: 'IS' | 'PROGRAM' | 'EPIC';
      quarter?: number;
      fiscalYear?: number;
      status?: string;
      epicId?: string;
      investibleStructureId?: string;
      programId?: string;
      parentId?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      const query = buildQueryString(params as any);
      return apiCall('GET', `/okrs/objectives${query ? `?${query}` : ''}`);
    },
    getById: (id: string) => apiCall('GET', `/okrs/objectives/${id}`),
    getTree: (params?: { quarter?: number; fiscalYear?: number }) => {
      const query = buildQueryString(params as any);
      return apiCall('GET', `/okrs/objectives/tree${query ? `?${query}` : ''}`);
    },
    create: (data: {
      title: string;
      description?: string;
      epicId?: string;
      investibleStructureId?: string;
      programId?: string;
      quarter: number;
      fiscalYear: number;
      status?: string;
      parentObjectiveId?: string;
    }) => apiCall('POST', '/okrs/objectives', data),
    update: (id: string, data: {
      title?: string;
      description?: string | null;
      quarter?: number;
      fiscalYear?: number;
      status?: string;
      parentObjectiveId?: string | null;
    }) => apiCall('PUT', `/okrs/objectives/${id}`, data),
    updateStatus: (id: string, status: string) =>
      apiCall('PUT', `/okrs/objectives/${id}/status`, { status }),
    delete: (id: string) => apiCall('DELETE', `/okrs/objectives/${id}`),
  },

  // Key Result endpoints
  keyResults: {
    getAll: (params?: {
      objectiveId?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      const query = buildQueryString(params as any);
      return apiCall('GET', `/okrs/key-results${query ? `?${query}` : ''}`);
    },
    create: (data: {
      description: string;
      targetValue: number;
      currentValue?: number;
      startValue?: number;
      unit?: string;
      confidence?: number;
      objectiveId: string;
    }) => apiCall('POST', '/okrs/key-results', data),
    update: (id: string, data: {
      description?: string;
      targetValue?: number;
      currentValue?: number | null;
      startValue?: number;
      unit?: string | null;
      confidence?: number | null;
    }) => apiCall('PUT', `/okrs/key-results/${id}`, data),
    delete: (id: string) => apiCall('DELETE', `/okrs/key-results/${id}`),
    recordProgress: (id: string, data: { value: number; note?: string }) =>
      apiCall('POST', `/okrs/key-results/${id}/progress`, data),
    getProgressHistory: (id: string, params?: { page?: number; limit?: number }) => {
      const query = buildQueryString(params as any);
      return apiCall('GET', `/okrs/key-results/${id}/history${query ? `?${query}` : ''}`);
    },
  },

  // Dashboard endpoints
  dashboard: {
    get: (params?: {
      fiscalYear?: number;
      quarter?: number;
      level?: 'IS' | 'PROGRAM' | 'EPIC' | 'ALL';
      investibleStructureId?: string;
      programId?: string;
    }) => {
      const query = buildQueryString(params as any);
      return apiCall('GET', `/okrs/dashboard${query ? `?${query}` : ''}`);
    },
    getCycles: () => apiCall('GET', '/okrs/dashboard/cycles'),
  },
};

export const businessOutcomesApi = {
  getAll: (params?: { epicId?: string; expectedDateFrom?: string; expectedDateTo?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/business-outcomes${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/business-outcomes/${id}`),
  create: (data: any) => apiCall('POST', '/business-outcomes', data),
  update: (id: string, data: any) => apiCall('PUT', `/business-outcomes/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/business-outcomes/${id}`),
  getFinancialSummary: () => apiCall('GET', '/business-outcomes/financial-summary'),
};

// DEPRECATED: allocationsApi removed - use epicsApi.getAllocations, createAllocation, etc.
// All allocation functionality is now in EpicAllocation (via epicsApi)

export const featureAllocationsApi = {
  getAll: (params?: { 
    featureId?: string; 
    programId?: string; 
    quarter?: number;
    scenarioId?: string;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/feature-allocations${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/feature-allocations/${id}`),
  create: (data: any) => apiCall('POST', '/feature-allocations', data),
  update: (id: string, data: any) => apiCall('PUT', `/feature-allocations/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/feature-allocations/${id}`),
  bulkUpdate: (allocations: any[]) => apiCall('PUT', '/feature-allocations/bulk-update', { allocations }),
  getQuarterlyAnalytics: (params?: { quarter?: number; scenarioId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/feature-allocations/quarterly-analytics${query ? `?${query}` : ''}`);
  },
};

export const scenariosApi = {
  getAll: (params?: { planningPeriodId?: string; createdById?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/scenarios${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/scenarios/${id}`),
  create: (data: any) => apiCall('POST', '/scenarios', data),
  update: (id: string, data: any) => apiCall('PUT', `/scenarios/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/scenarios/${id}`),
  clone: (id: string, data: any) => apiCall('POST', `/scenarios/${id}/clone`, data),
  compare: (scenarioIds: string[]) => apiCall('POST', '/scenarios/compare', { scenarioIds }),
  setBaseline: (id: string) => apiCall('PUT', `/scenarios/${id}/set-baseline`),

  // Optimization endpoints
  getSupplyDemand: (id: string, programId?: string) => {
    const query = programId ? `?programId=${programId}` : '';
    return apiCall('GET', `/scenarios/${id}/supply-demand${query}`);
  },
  getTeamCapacity: (id: string, programId?: string) => {
    const query = programId ? `?programId=${programId}` : '';
    return apiCall('GET', `/scenarios/${id}/team-capacity${query}`);
  },
  optimize: (id: string, data: {
    constraints?: {
      maxTeamUtilization?: number;
      minBusinessAllocation?: number;
      minTechAllocation?: number;
      minSupportAllocation?: number;
    };
    priorities?: Array<{ epicId: string; priority: number }>;
  }) => apiCall('POST', `/scenarios/${id}/optimize`, data),
  applySuggestions: (id: string, suggestions: Array<{
    epicId: string;
    epicName?: string;
    programId: string;
    programName?: string;
    teamId: string;
    teamName?: string;
    allocatedWeeks: number;
    reason?: string;
  }>) => apiCall('POST', `/scenarios/${id}/apply-suggestions`, { suggestions }),
  compareDetailed: (id: string, otherScenarioId: string) =>
    apiCall('GET', `/scenarios/${id}/compare/${otherScenarioId}`),

  // Budget analysis endpoints
  getBudgetAnalysis: (id: string, programId: string, fiscalYear?: number) => {
    let query = `?programId=${programId}`;
    if (fiscalYear) query += `&fiscalYear=${fiscalYear}`;
    return apiCall('GET', `/scenarios/${id}/budget-analysis${query}`);
  },
  upsertTeamAdjustment: (scenarioId: string, teamId: string, data: { adjustedMemberCount: number; isReleased?: boolean; adjustedCostProfileId?: string; notes?: string }) =>
    apiCall('PUT', `/scenarios/${scenarioId}/team-adjustments/${teamId}`, data),
  deleteTeamAdjustment: (scenarioId: string, teamId: string) =>
    apiCall('DELETE', `/scenarios/${scenarioId}/team-adjustments/${teamId}`),
  upsertEpicEstimate: (scenarioId: string, epicId: string, data: { adjustedTeamWeeks: number; notes?: string }) =>
    apiCall('PUT', `/scenarios/${scenarioId}/epic-estimates/${epicId}`, data),
  deleteEpicEstimate: (scenarioId: string, epicId: string) =>
    apiCall('DELETE', `/scenarios/${scenarioId}/epic-estimates/${epicId}`),

  // Recommendations endpoint
  getRecommendations: (id: string, programId?: string) => {
    const query = programId ? `?programId=${programId}` : '';
    return apiCall('GET', `/scenarios/${id}/recommendations${query}`);
  },

  // Virtual team endpoints
  getVirtualTeams: (scenarioId: string, programId?: string) => {
    const query = programId ? `?programId=${programId}` : '';
    return apiCall('GET', `/scenarios/${scenarioId}/virtual-teams${query}`);
  },
  createVirtualTeam: (scenarioId: string, data: { programId: string; name: string; costProfileId: string; memberCount: number; notes?: string }) =>
    apiCall('POST', `/scenarios/${scenarioId}/virtual-teams`, data),
  deleteVirtualTeam: (scenarioId: string, virtualTeamId: string) =>
    apiCall('DELETE', `/scenarios/${scenarioId}/virtual-teams/${virtualTeamId}`),
  createVirtualTeamAllocation: (scenarioId: string, virtualTeamId: string, data: { epicId: string; teamWeeks: number; notes?: string }) =>
    apiCall('POST', `/scenarios/${scenarioId}/virtual-teams/${virtualTeamId}/allocations`, data),
  deleteVirtualTeamAllocation: (scenarioId: string, virtualTeamId: string, allocationId: string) =>
    apiCall('DELETE', `/scenarios/${scenarioId}/virtual-teams/${virtualTeamId}/allocations/${allocationId}`),
};

export const dependenciesApi = {
  getAll: (params?: { 
    entityType?: string; 
    entityId?: string; 
    status?: string;
    severity?: string;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/dependencies${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/dependencies/${id}`),
  create: (data: any) => apiCall('POST', '/dependencies', data),
  update: (id: string, data: any) => apiCall('PUT', `/dependencies/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/dependencies/${id}`),
  updateStatus: (id: string, status: string) => apiCall('PUT', `/dependencies/${id}/status`, { status }),
  getAtRisk: () => apiCall('GET', '/dependencies/at-risk'),
};

export const capacityApi = {
  getPortfolioCapacity: (params?: { planningPeriodId?: string; scenarioId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/capacity/portfolio${query ? `?${query}` : ''}`);
  },
  getProgramCapacity: (programId: string, params?: { planningPeriodId?: string; scenarioId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/capacity/program/${programId}${query ? `?${query}` : ''}`);
  },
  getTeamCapacity: (teamId: string, params?: { planningPeriodId?: string; scenarioId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/capacity/team/${teamId}${query ? `?${query}` : ''}`);
  },
  getCategoryCompliance: (params?: { planningPeriodId?: string; scenarioId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/capacity/category-compliance${query ? `?${query}` : ''}`);
  },
  getQuarterlyCapacity: (params?: { planningPeriodId?: string; scenarioId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/capacity/quarterly${query ? `?${query}` : ''}`);
  },
  validateAllocations: (params?: { planningPeriodId?: string; scenarioId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/capacity/validate-allocations${query ? `?${query}` : ''}`);
  },
};

export const dashboardApi = {
  getExecutiveDashboard: (params?: { planningPeriodId?: string; scenarioId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/executive-dashboard${query ? `?${query}` : ''}`);
  },
  getISOwnerDashboard: (params?: {
    investibleStructureId?: string;
    planningPeriodId?: string;
    scenarioId?: string
  }) => {
    // Map frontend params to backend params
    const backendParams: any = {};
    if (params?.investibleStructureId) {
      backendParams.isId = params.investibleStructureId;
    }
    if (params?.scenarioId) {
      backendParams.scenarioId = params.scenarioId;
    }
    // Note: planningPeriodId is not used by backend IS Owner dashboard

    const query = buildQueryString(backendParams);
    return apiCall('GET', `/is-owner-dashboard${query ? `?${query}` : ''}`);
  },
  getProgramLeadDashboard: (params?: { 
    programId?: string; 
    planningPeriodId?: string; 
    scenarioId?: string 
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/program-lead-dashboard${query ? `?${query}` : ''}`);
  },
  
  // Capacity utilization endpoints
  getCapacityUtilization: (params?: { programId?: string; quarter?: number }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/capacity/utilization${query ? `?${query}` : ''}`);
  },
  getProgramCapacity: (programId: string) => apiCall('GET', `/programs/${programId}/allocations`),
  getTeamCapacity: (teamId: string) => apiCall('GET', `/teams/${teamId}/allocations`),
};

export const notificationsApi = {
  getAll: (params?: { isRead?: boolean; page?: number; limit?: number }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/notifications${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/notifications/${id}`),
  markAsRead: (id: string) => apiCall('PUT', `/notifications/${id}/read`),
  markAsUnread: (id: string) => apiCall('PUT', `/notifications/${id}/unread`),
  bulkMarkAsRead: (ids: string[]) => apiCall('PUT', '/notifications/bulk-read', { ids }),
  delete: (id: string) => apiCall('DELETE', `/notifications/${id}`),
  getUnreadCount: () => apiCall('GET', '/notifications/unread-count'),
  getSummary: () => apiCall('GET', '/notifications/summary'),
};

export const auditLogsApi = {
  getAll: (params?: { 
    entityType?: string; 
    entityId?: string; 
    userId?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/audit-logs${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/audit-logs/${id}`),
  getByEntity: (entityType: string, entityId: string) => 
    apiCall('GET', `/audit-logs/entity/${entityType}/${entityId}`),
  getByUser: (userId: string, params?: { startDate?: string; endDate?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/audit-logs/user/${userId}${query ? `?${query}` : ''}`);
  },
  getStatistics: (params?: { startDate?: string; endDate?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/audit-logs/statistics${query ? `?${query}` : ''}`);
  },
};

export const usersApi = {
  getAll: (params?: { 
    role?: string; 
    isActive?: boolean; 
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/users${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/users/${id}`),
  create: (data: any) => apiCall('POST', '/users', data),
  update: (id: string, data: any) => apiCall('PUT', `/users/${id}`, data),
  activate: (id: string) => apiCall('PUT', `/users/${id}/activate`),
  deactivate: (id: string) => apiCall('PUT', `/users/${id}/deactivate`),
  delete: (id: string) => apiCall('DELETE', `/users/${id}`),
  getRoles: () => apiCall('GET', '/users/roles'),
};

export const searchApi = {
  globalSearch: (params: {
    query?: string;
    entityTypes?: string[];
    filters?: any;
    limit?: number;
    offset?: number;
    includeHighlights?: boolean;
    fuzzySearch?: boolean;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/search/global${query ? `?${query}` : ''}`);
  },
  
  entitySearch: (entityType: string, params: {
    query?: string;
    filters?: any;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/search/${entityType}${query ? `?${query}` : ''}`);
  },
  
  autocomplete: (params: {
    query: string;
    entityTypes?: string[];
    limit?: number;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/search/autocomplete${query ? `?${query}` : ''}`);
  },
  
  suggestions: (params: {
    query: string;
    entityType?: string;
    limit?: number;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/search/suggestions${query ? `?${query}` : ''}`);
  },
  
  facets: (params: {
    query?: string;
    entityTypes?: string[];
    filters?: any;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/search/facets${query ? `?${query}` : ''}`);
  },
  
  analytics: () => apiCall('GET', '/search/analytics'),
  
  // Filter presets
  getPresets: (params?: { entityType?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/search/presets${query ? `?${query}` : ''}`);
  },
  
  createPreset: (data: {
    name: string;
    filters: any;
    entityType?: string;
    isDefault?: boolean;
  }) => apiCall('POST', '/search/presets', data),
  
  updatePreset: (id: string, data: {
    name?: string;
    filters?: any;
    isDefault?: boolean;
  }) => apiCall('PUT', `/search/presets/${id}`, data),
  
  deletePreset: (id: string) => apiCall('DELETE', `/search/presets/${id}`),
  
  // Saved searches
  getSavedSearches: () => apiCall('GET', '/search/saved'),
  
  createSavedSearch: (data: {
    name: string;
    query: string;
    filters: any;
    entityType?: string;
  }) => apiCall('POST', '/search/saved', data),
  
  updateSavedSearch: (id: string, data: {
    name?: string;
    query?: string;
    filters?: any;
    isStarred?: boolean;
  }) => apiCall('PUT', `/search/saved/${id}`, data),
  
  deleteSavedSearch: (id: string) => apiCall('DELETE', `/search/saved/${id}`),
  
  // Search history and recent searches
  getRecentSearches: (limit?: number) => {
    const query = limit ? `?limit=${limit}` : '';
    return apiCall('GET', `/search/recent${query}`);
  },
  
  recordSearch: (data: {
    query: string;
    filters?: any;
    entityType?: string;
    resultCount: number;
  }) => apiCall('POST', '/search/record', data),
};

export const timelineApi = {
  getTimelineData: (params?: {
    investibleStructureId?: string;
    planningPeriodId?: string;
  }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/timeline${query ? `?${query}` : ''}`);
  },

  getInvestibleStructures: () => apiCall('GET', '/timeline/investible-structures'),
};

export const divisionsApi = {
  getAll: () => apiCall('GET', '/divisions'),
  getById: (id: string) => apiCall('GET', `/divisions/${id}`),
  create: (data: any) => apiCall('POST', '/divisions', data),
  update: (id: string, data: any) => apiCall('PUT', `/divisions/${id}`, data),
  updateBudget: (id: string, data: { annualBudget?: number; budgetFiscalYear?: number }) =>
    apiCall('PUT', `/divisions/${id}/budget`, data),
  delete: (id: string) => apiCall('DELETE', `/divisions/${id}`),
  getBudget: (id: string) => apiCall('GET', `/divisions/${id}/budget`),
  getBudgetSummary: (id: string, fiscalYear?: number) => {
    const query = fiscalYear ? `?fiscalYear=${fiscalYear}` : '';
    return apiCall('GET', `/divisions/${id}/budget-summary${query}`);
  },
};

export const resourcesApi = {
  getAll: (params?: { type?: string; level?: string; active?: boolean; page?: number; limit?: number }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/resources${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/resources/${id}`),
  create: (data: any) => apiCall('POST', '/resources', data),
  update: (id: string, data: any) => apiCall('PUT', `/resources/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/resources/${id}`),
};

export const resourceTeamAssignmentsApi = {
  getAll: (params?: { resourceId?: string; teamId?: string; active?: boolean }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/resource-team-assignments${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/resource-team-assignments/${id}`),
  create: (data: any) => apiCall('POST', '/resource-team-assignments', data),
  update: (id: string, data: any) => apiCall('PUT', `/resource-team-assignments/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/resource-team-assignments/${id}`),
  transfer: (data: { resourceId: string; newTeamId: string; transferDate: string }) =>
    apiCall('POST', '/resource-team-assignments/transfer', data),
};

export const rateCardsApi = {
  getAll: () => apiCall('GET', '/rate-cards'),
  getByYear: (year: number) => apiCall('GET', `/rate-cards/year/${year}`),
  getById: (id: string) => apiCall('GET', `/rate-cards/${id}`),
  create: (data: any) => apiCall('POST', '/rate-cards', data),
  update: (id: string, data: any) => apiCall('PUT', `/rate-cards/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/rate-cards/${id}`),
};

export const resourceLevelsApi = {
  getAll: (includeInactive?: boolean) => {
    const query = includeInactive ? '?includeInactive=true' : '';
    return apiCall('GET', `/resource-levels${query}`);
  },
  getById: (id: string) => apiCall('GET', `/resource-levels/${id}`),
  create: (data: any) => apiCall('POST', '/resource-levels', data),
  update: (id: string, data: any) => apiCall('PUT', `/resource-levels/${id}`, data),
  delete: (id: string) => apiCall('DELETE', `/resource-levels/${id}`),
};

export const resourceIntakeRequestsApi = {
  getAll: (params?: { status?: string; requestedBy?: string; mine?: boolean; programId?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/resource-intake-requests${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiCall('GET', `/resource-intake-requests/${id}`),
  create: (data: any) => apiCall('POST', '/resource-intake-requests', data),
  update: (id: string, data: any) => apiCall('PUT', `/resource-intake-requests/${id}`, data),
  approve: (id: string, overrideBudgetWarning?: boolean) =>
    apiCall('POST', `/resource-intake-requests/${id}/approve`, { overrideBudgetWarning }),
  decline: (id: string, reason?: string) =>
    apiCall('POST', `/resource-intake-requests/${id}/decline`, { reason }),

  // Validation endpoints
  validate: (data: {
    resourceLevel?: string;
    resourceLevelId?: string;
    programId?: string;
    resourceType?: string;
    businessJustification?: string;
  }) => apiCall('POST', '/resource-intake-requests/validate', data),

  getBudgetImpact: (programId?: string) => {
    const query = programId ? `?programId=${programId}` : '';
    return apiCall('GET', `/resource-intake-requests/budget-impact${query}`);
  },
};

// Cost Management APIs
export const costsApi = {
  // Program Budget
  getBudget: (programId: string) => apiCall('GET', `/programs/${programId}/budget`),
  updateBudget: (programId: string, data: {
    annualBudget?: number;
    operationalOverhead?: number;
    laborBudget?: number;
    nonLaborBudget?: number;
    budgetFiscalYear?: number;
  }) => apiCall('PUT', `/programs/${programId}/budget`, data),

  // Operational Cost Entries
  getCostEntries: (programId: string, params?: { fiscalYear?: number; quarter?: number; category?: string }) => {
    const query = buildQueryString(params as any);
    return apiCall('GET', `/programs/${programId}/operational-costs${query ? `?${query}` : ''}`);
  },
  createCostEntry: (programId: string, data: {
    category: string;
    description: string;
    amount: number;
    fiscalYear: number;
    quarter: number;
    month?: number;
  }) => apiCall('POST', `/programs/${programId}/operational-costs`, data),
  updateCostEntry: (costId: string, data: {
    category?: string;
    description?: string;
    amount?: number;
    fiscalYear?: number;
    quarter?: number;
    month?: number;
  }) => apiCall('PUT', `/operational-costs/${costId}`, data),
  deleteCostEntry: (costId: string) => apiCall('DELETE', `/operational-costs/${costId}`),

  // Cost Summaries
  getCostSummary: (programId: string) => apiCall('GET', `/programs/${programId}/cost-summary`),
  getCostDashboard: () => apiCall('GET', '/cost-dashboard'),
  getDivisionCostSummary: (divisionId: string) => apiCall('GET', `/cost-dashboard/divisions/${divisionId}/cost-summary`),
};

export default api;
export { api };
